// @ts-nocheck
import { useMutation, useQuery } from "@tanstack/react-query";
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import type {
  ErrorResponse,
  PatchedSessionUpdate,
  PremadeStoryConceptDetail,
  SessionCreate,
  SessionSubmitResponse,
  SessionsPremadeConceptsListParams,
  StoryCreationSession,
  ValidationErrorResponse,
} from "../../models";
import { customInstance } from "../../custom-instance";

type SecondParameter<T extends (...args: any) => any> = Parameters<T>[1];

/**
 * Initializes a new interactive story refinement session. Users can then refine the plot through chat before generating the full story.
 * @summary Create a new story creation session
 */
export const sessionsCreate = (
  sessionCreate: SessionCreate,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryCreationSession>(
    {
      url: `/api/sessions/`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      data: sessionCreate,
      signal,
    },
    options
  );
};

export const getSessionsCreateMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsCreate>>,
    TError,
    { data: SessionCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sessionsCreate>>,
  TError,
  { data: SessionCreate },
  TContext
> => {
  const mutationKey = ["sessionsCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sessionsCreate>>,
    { data: SessionCreate }
  > = (props) => {
    const { data } = props ?? {};

    return sessionsCreate(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SessionsCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof sessionsCreate>>
>;
export type SessionsCreateMutationBody = SessionCreate;
export type SessionsCreateMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Create a new story creation session
 */
export const useSessionsCreate = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsCreate>>,
    TError,
    { data: SessionCreate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof sessionsCreate>>,
  TError,
  { data: SessionCreate },
  TContext
> => {
  const mutationOptions = getSessionsCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Retrieve the current state of a story creation session
 * @summary Get session details
 */
export const sessionsRetrieve = (
  uuid: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<StoryCreationSession>(
    { url: `/api/sessions/${uuid}/`, method: "GET", signal },
    options
  );
};

export const getSessionsRetrieveQueryKey = (uuid: string) => {
  return [`/api/sessions/${uuid}/`] as const;
};

export const getSessionsRetrieveQueryOptions = <
  TData = Awaited<ReturnType<typeof sessionsRetrieve>>,
  TError = ErrorResponse
>(
  uuid: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getSessionsRetrieveQueryKey(uuid);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof sessionsRetrieve>>
  > = ({ signal }) => sessionsRetrieve(uuid, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: !!uuid,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof sessionsRetrieve>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SessionsRetrieveQueryResult = NonNullable<
  Awaited<ReturnType<typeof sessionsRetrieve>>
>;
export type SessionsRetrieveQueryError = ErrorResponse;

export function useSessionsRetrieve<
  TData = Awaited<ReturnType<typeof sessionsRetrieve>>,
  TError = ErrorResponse
>(
  uuid: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof sessionsRetrieve>>,
          TError,
          Awaited<ReturnType<typeof sessionsRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessionsRetrieve<
  TData = Awaited<ReturnType<typeof sessionsRetrieve>>,
  TError = ErrorResponse
>(
  uuid: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsRetrieve>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof sessionsRetrieve>>,
          TError,
          Awaited<ReturnType<typeof sessionsRetrieve>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessionsRetrieve<
  TData = Awaited<ReturnType<typeof sessionsRetrieve>>,
  TError = ErrorResponse
>(
  uuid: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Get session details
 */

export function useSessionsRetrieve<
  TData = Awaited<ReturnType<typeof sessionsRetrieve>>,
  TError = ErrorResponse
>(
  uuid: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsRetrieve>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getSessionsRetrieveQueryOptions(uuid, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Update character selections or art style for a session
 * @summary Update session selections
 */
export const sessionsPartialUpdate = (
  uuid: string,
  patchedSessionUpdate: PatchedSessionUpdate,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<StoryCreationSession>(
    {
      url: `/api/sessions/${uuid}/`,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      data: patchedSessionUpdate,
    },
    options
  );
};

export const getSessionsPartialUpdateMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsPartialUpdate>>,
    TError,
    { uuid: string; data: PatchedSessionUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sessionsPartialUpdate>>,
  TError,
  { uuid: string; data: PatchedSessionUpdate },
  TContext
> => {
  const mutationKey = ["sessionsPartialUpdate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sessionsPartialUpdate>>,
    { uuid: string; data: PatchedSessionUpdate }
  > = (props) => {
    const { uuid, data } = props ?? {};

    return sessionsPartialUpdate(uuid, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SessionsPartialUpdateMutationResult = NonNullable<
  Awaited<ReturnType<typeof sessionsPartialUpdate>>
>;
export type SessionsPartialUpdateMutationBody = PatchedSessionUpdate;
export type SessionsPartialUpdateMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Update session selections
 */
export const useSessionsPartialUpdate = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsPartialUpdate>>,
    TError,
    { uuid: string; data: PatchedSessionUpdate },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof sessionsPartialUpdate>>,
  TError,
  { uuid: string; data: PatchedSessionUpdate },
  TContext
> => {
  const mutationOptions = getSessionsPartialUpdateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancel a story creation session
 * @summary Delete/cancel session
 */
export const sessionsDestroy = (
  uuid: string,
  options?: SecondParameter<typeof customInstance>
) => {
  return customInstance<unknown>(
    { url: `/api/sessions/${uuid}/`, method: "DELETE" },
    options
  );
};

export const getSessionsDestroyMutationOptions = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsDestroy>>,
    TError,
    { uuid: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sessionsDestroy>>,
  TError,
  { uuid: string },
  TContext
> => {
  const mutationKey = ["sessionsDestroy"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sessionsDestroy>>,
    { uuid: string }
  > = (props) => {
    const { uuid } = props ?? {};

    return sessionsDestroy(uuid, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SessionsDestroyMutationResult = NonNullable<
  Awaited<ReturnType<typeof sessionsDestroy>>
>;

export type SessionsDestroyMutationError = ErrorResponse;

/**
 * @summary Delete/cancel session
 */
export const useSessionsDestroy = <
  TError = ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsDestroy>>,
    TError,
    { uuid: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof sessionsDestroy>>,
  TError,
  { uuid: string },
  TContext
> => {
  const mutationOptions = getSessionsDestroyMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Finalize the session and create the actual Story with full image generation. This will deduct credits and cannot be undone.
 * @summary Submit for full story generation
 */
export const sessionsSubmitCreate = (
  uuid: string,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<SessionSubmitResponse>(
    { url: `/api/sessions/${uuid}/submit/`, method: "POST", signal },
    options
  );
};

export const getSessionsSubmitCreateMutationOptions = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsSubmitCreate>>,
    TError,
    { uuid: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof sessionsSubmitCreate>>,
  TError,
  { uuid: string },
  TContext
> => {
  const mutationKey = ["sessionsSubmitCreate"];
  const { mutation: mutationOptions, request: requestOptions } = options
    ? options.mutation &&
      "mutationKey" in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, request: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof sessionsSubmitCreate>>,
    { uuid: string }
  > = (props) => {
    const { uuid } = props ?? {};

    return sessionsSubmitCreate(uuid, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type SessionsSubmitCreateMutationResult = NonNullable<
  Awaited<ReturnType<typeof sessionsSubmitCreate>>
>;

export type SessionsSubmitCreateMutationError =
  | ValidationErrorResponse
  | ErrorResponse;

/**
 * @summary Submit for full story generation
 */
export const useSessionsSubmitCreate = <
  TError = ValidationErrorResponse | ErrorResponse,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof sessionsSubmitCreate>>,
    TError,
    { uuid: string },
    TContext
  >;
  request?: SecondParameter<typeof customInstance>;
}): UseMutationResult<
  Awaited<ReturnType<typeof sessionsSubmitCreate>>,
  TError,
  { uuid: string },
  TContext
> => {
  const mutationOptions = getSessionsSubmitCreateMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Returns active premade story concepts that have at least one variant matching the given num_characters and language. Each concept includes a matched_variant_id pointing to the best matching PremadeStory variant.
 * @summary List premade story concepts
 */
export const sessionsPremadeConceptsList = (
  params: SessionsPremadeConceptsListParams,
  options?: SecondParameter<typeof customInstance>,
  signal?: AbortSignal
) => {
  return customInstance<PremadeStoryConceptDetail[]>(
    { url: `/api/sessions/premade-concepts/`, method: "GET", params, signal },
    options
  );
};

export const getSessionsPremadeConceptsListQueryKey = (
  params: SessionsPremadeConceptsListParams
) => {
  return [
    `/api/sessions/premade-concepts/`,
    ...(params ? [params] : []),
  ] as const;
};

export const getSessionsPremadeConceptsListQueryOptions = <
  TData = Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
  TError = unknown
>(
  params: SessionsPremadeConceptsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getSessionsPremadeConceptsListQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof sessionsPremadeConceptsList>>
  > = ({ signal }) =>
    sessionsPremadeConceptsList(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type SessionsPremadeConceptsListQueryResult = NonNullable<
  Awaited<ReturnType<typeof sessionsPremadeConceptsList>>
>;
export type SessionsPremadeConceptsListQueryError = unknown;

export function useSessionsPremadeConceptsList<
  TData = Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
  TError = unknown
>(
  params: SessionsPremadeConceptsListParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
          TError,
          Awaited<ReturnType<typeof sessionsPremadeConceptsList>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessionsPremadeConceptsList<
  TData = Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
  TError = unknown
>(
  params: SessionsPremadeConceptsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
          TError,
          Awaited<ReturnType<typeof sessionsPremadeConceptsList>>
        >,
        "initialData"
      >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useSessionsPremadeConceptsList<
  TData = Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
  TError = unknown
>(
  params: SessionsPremadeConceptsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary List premade story concepts
 */

export function useSessionsPremadeConceptsList<
  TData = Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
  TError = unknown
>(
  params: SessionsPremadeConceptsListParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof sessionsPremadeConceptsList>>,
        TError,
        TData
      >
    >;
    request?: SecondParameter<typeof customInstance>;
  }
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getSessionsPremadeConceptsListQueryOptions(
    params,
    options
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
